import React, { useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import {
  type GridSelectionModel,
  type GridFilterModel,
  type GridSortModel,
} from "@material-ui/x-grid";

import TimeFilter, { getPeriodFromPreset } from "src/components/TimeFilter";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { useDateRange } from "src/utils/timeFilter";
import TableHeader from "src/components/TableHeader";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { useFullscreen } from "src/utils/fullscreen";
import { mappedTransactionSelector } from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import makeRenderCell from "src/utils/makeRenderCell";
import { formatAbsCurrency } from "src/utils/formatter";
import { makeTransactionsStyle } from "src/utils/gridCellStyles";
import renderHeaderLabel from "src/utils/renderHeaderLabel";
import { withGridStringOperatorsWithNotContains } from "src/utils/gridStringFilters";

const AllTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - All" },
});

export const Columns = withGridStringOperatorsWithNotContains([
  {
    field: "id",
    hide: true,
  },
  {
    field: "date",
    headerName: "Date",
    type: "date",
    width: 200,
  },
  {
    field: "general_ledger_account",
    headerName: "General Ledger Account",
    width: 250,
    description:
      "The general ledger account is a tag that categorizes a transaction. This is used to build accounting reports and organize information.",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "bank_account",
    headerName: "Bank Account",
    width: 250,
    description: "The card or account used to make the purchase.",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "charge_description",
    headerName: "Charge Description",
    width: 200,
    type: "string",
  },
  {
    field: "description",
    headerName: "Description",
    width: 250,
    description: "A note to add information about the transaction.",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 200,
    type: "number",
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
]);

function All() {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLDivElement>();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const requestFullscreen = useFullscreen();
  const filters = useMemo(() => {
    const models: GridFilterModel[] = [];
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);

    const model = {
      items: [
        {
          columnField: "date",
          operatorValue: "onOrAfter",
          value: calendarYmdInTimeZone(period.start, timeZone),
        },
        {
          columnField: "date",
          operatorValue: "onOrBefore",
          value: calendarYmdInTimeZone(period.end, timeZone),
        },
      ],
    };

    models.push(model);

    return models;
  }, [timeFilter, timeZone]);
  const transactions = useSelector((state) =>
    mappedTransactionSelector(state, filters)
  );
  return (
    <div>
      <TimeFilter value={timeFilter} onChange={(v) => setDateRange(v)} />
      <Box
        {...({ ref: sectionRef } as any)}
        component={Paper}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader title="All Transactions" />
        <XGrid
          checkboxSelection
          tableName="all"
          Component={CSVFormattedXGrid}
          rows={transactions}
          columns={Columns}
          headerHeight={35}
          rowHeight={35}
          components={{
            Toolbar: AllTableToolbar,
          }}
          componentsProps={{
            toolbar: {
              onFullscreenClick: () => {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                  return;
                }
                if (!sectionRef.current) return;
                requestFullscreen(sectionRef.current);
                recordEvent({
                  type: "feature-adoption",
                  feature: "fullscreen",
                  element: "all-table",
                });
              },
              onQuickFilterBlur: (e) => {
                const text = e.target.value;
                if (!text) return;
                recordEvent({
                  type: "feature-adoption",
                  feature: "quick-filter",
                  element: "all",
                  text,
                });
              },
            },
          }}
          onFilterModelChange={(model) => {
            recordEvent({
              type: "feature-adoption",
              feature: "table-filter",
              element: "all",
              filter: JSON.stringify(model),
            });
          }}
          selectionModel={selectionModel}
          onSelectionModelChange={(selectionModel: GridSelectionModel) => {
            setSelectionModel(selectionModel);
          }}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          onCellDoubleClick={(ev) => {
            if (ev.row?.journal_entry_id) {
              dispatch(
                setActiveDialog("add_journal", {
                  id: ev.row.journal_entry_id,
                })
              );
              return;
            }
            if (ev.row?.split_group_id) {
              dispatch(
                setActiveDialog("edit_split", {
                  split_group_id: ev.row.split_group_id,
                  id: ev.row.collection_id || ev.row.id,
                })
              );
              return;
            }
            if (ev.row.collection === "transaction") {
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: ev.row.collection_id,
                })
              );
              return;
            }

            if (ev.row.collection === "inventory") {
              dispatch(
                setActiveDialog("edit_inventory", { id: ev.row.collection_id })
              );
              return;
            }

            if (ev.row.collection === "sale") {
              dispatch(
                setActiveDialog("edit_sale", { id: ev.row.collection_id })
              );
              return;
            }
          }}
        />
      </Box>
    </div>
  );
}

export default All;
